import {
  ReactElement, useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Button, Loader,
} from 'components';
import { apiCall, ApiResps } from 'providers';
import { useRouteMatch } from 'react-router-dom';
import { colors } from 'styles/colors';
import {
  AdditionalInfoContainer, ButtonContainer, ClientHeaderContainer, ClientInfoContainer, CNPJIcon, InfoContainer, PictureContainer, TitleContainer,
} from './styles';
import { ToggleSwitchMini } from 'components/ToggleSwitch';
import {
  Edit5Icon, Mail2Icon, PhoneIcon, Settings2Icon,
} from '~/icons';
import { useTranslation } from 'react-i18next';
import { ClientModal } from '../ClientModal';
import { buildClientType, buildFormDataByInfo, buildParamsByFormData } from '../Form/constants';
import { ClientType, EditClientFormValues } from '../Form/types';
import { useToast } from 'contexts/newToastContext';

interface ClientHeaderProps {
  clientInfo: ApiResps['/clients/get-client-info']['client'] | undefined;
  loading: boolean;
}

export interface Info {
    picture: string,
    clientType: ClientType,
    email: string,
    phone: string,
    name: string,
    enabled: boolean,
    cnpj: string
  }

function ClientInfo({
  icon,
  value,
}): ReactElement {
  function renderIcon(): ReactElement {
    switch (icon) {
      case 'cnpj':
        return <CNPJIcon>CNPJ</CNPJIcon>;
      case 'email':
        return <Mail2Icon size="16" />;
      case 'phone':
        return <PhoneIcon size="16" />;
      case 'settings':
        return <Settings2Icon size="16" />;
      default: throw new Error('Icon not found');
    }
  }

  return (
    <div>
      {renderIcon()}
      <span>{value}</span>
    </div>
  );
}

export function ClientHeader({
  clientInfo,
  loading,
}: Readonly<ClientHeaderProps>): ReactElement {
  const { t } = useTranslation();
  const match = useRouteMatch<{ clientId: string }>();
  const { toast } = useToast();
  const fabricante = match.url.includes('fabricantes');
  const [info, setInfo] = useState<Info>();
  const [openEditModal, setOpenEditModal] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const setDefaultInfo = useCallback((): void => {
    if (clientInfo) {
      setInfo({
        clientType: buildClientType(clientInfo.clientType, fabricante),
        enabled: clientInfo.ENABLED === '1',
        email: clientInfo.EMAIL,
        name: clientInfo.NAME,
        phone: clientInfo.PHONE,
        picture: clientInfo.PICTURE,
        cnpj: clientInfo.CNPJ,
      });
    }
  }, [clientInfo]);

  function onClickEditButton(): void {
    setOpenEditModal(true);
  }

  function onCloseEditModal(): void {
    setOpenEditModal(false);
  }

  function onSuccessEdit(data: EditClientFormValues): void {
    setInfo({
      clientType: data.clientType as ClientType,
      cnpj: data.cnpj ? data.cnpj : '-',
      email: data.email ? data.email : '-',
      enabled: data.enabled,
      name: data.name,
      phone: data.phone ? data.phone : '-',
      picture: data.picture,
    });

    onCloseEditModal();
  }

  async function onClickToggleStatus(): Promise<void> {
    try {
      if (!info) return;
      setLoadingStatus(true);
      const newStatus = !info.enabled;
      const data = buildFormDataByInfo({
        ...info,
        enabled: newStatus,
      });
      const params = buildParamsByFormData(data);

      await apiCall('/clients/edit-client', {
        ...params,
        CLIENT_ID: Number(match.params.clientId),
      });

      setInfo({
        ...info,
        enabled: newStatus,
      });

      toast(t('sucessoTitulo'), t('clienteAlteradoSucesso'), 'success');
    } catch (error) {
      console.log(error);
      toast(t('erroTitulo'), t('houveErro'), 'error');
    }

    setLoadingStatus(false);
  }

  const clientInfos = useMemo(() => ([
    {
      icon: 'email',
      value: info?.email || '-',
    },
    {
      icon: 'phone',
      value: info?.phone || '-',
    },
    {
      icon: 'cnpj',
      value: info?.cnpj || '-',
    },
    {
      icon: 'settings',
      value: t(info?.clientType ?? 'cliente'),
    },
  ]), [info]);

  useEffect(() => {
    setDefaultInfo();
  }, [clientInfo, setDefaultInfo]);

  if (loading || !clientInfo || !info) {
    return (
      <Loader />
    );
  }

  return (
    <>
      <ClientModal
        info={info}
        isOpen={openEditModal}
        onClose={onCloseEditModal}
        onSuccess={onSuccessEdit}
      />
      <ClientHeaderContainer>
        <PictureContainer>
          {!!info.picture && (<img src={info.picture} alt="client-logo" />)}
        </PictureContainer>
        <InfoContainer>
          <ClientInfoContainer>
            <TitleContainer>
              <h1>{info.name}</h1>
              <ToggleSwitchMini
                variant="default-thin"
                checked={info.enabled}
                label={t(info.enabled ? 'habilitado' : 'desabilitado')}
                onClick={onClickToggleStatus}
                disabled={loadingStatus}
              />
            </TitleContainer>
            <AdditionalInfoContainer>
              {clientInfos.map((item) => (
                <ClientInfo
                  icon={item.icon}
                  value={item.value}
                  key={item.icon}
                />
              ))}
            </AdditionalInfoContainer>
          </ClientInfoContainer>
          <Button
            type="button"
            variant="primary-stroke"
            onClick={onClickEditButton}
            disabled={loadingStatus}
          >
            <ButtonContainer>
              <Edit5Icon size="14" color={colors.icon['primary-base']} />
              <span>{t('editar')}</span>
            </ButtonContainer>
          </Button>
        </InfoContainer>
      </ClientHeaderContainer>
    </>
  );
}
